import { useState } from "react";
import Trans from "@/i18n/Trans";
import { Router } from "@/router";
import { css, cx } from "@styled-system/css";
import { Flex, Box } from "@styled-system/jsx";
import { button } from "@styled-system/recipes";
import PageContainer from "@/components/react/PageContainer";
import Link from "@/components/react/Link";
import Spinner from "@/components/react/Spinner";
import Input from "@/components/react/Input";
import useI18n from "@/hooks/useI18n";
import { emails } from "@/modules/environment";
import { validatePromoCode } from "@/modules/cloud/giftcard";
import forUserForGuest from "@/utils/forUserForGuest";
import { getAppValue } from "@/utils/getAppValue";

type Props = {
  title: string;
  subtitle: string;
  imageName: string;
};

export default function Coupon({ title, subtitle, imageName }: Props) {
  const query = Router.getQuery();
  const [state, setState] = useState({
    code: query?.code || "",
    codeStatus: "none",
    loading: false,
    hiddenField: ""
  });
  const { code, codeStatus, loading } = state;
  const { t } = useI18n();

  async function handleCodeValidation(e: React.FormEvent) {
    e.preventDefault();
    if (state.hiddenField !== "") return;
    setState((prev) => ({ ...prev, loading: true, codeStatus: "none" }));

    try {
      const response = await validatePromoCode({ promoCode: state.code });
      let newCodeStatus = "error";
      if (response === null) newCodeStatus = "invalid";
      else if (response.error?.includes("expired")) newCodeStatus = "redeemed";
      else if (response) newCodeStatus = "valid";

      setState((prev) => ({
        ...prev,
        loading: false,
        codeStatus: newCodeStatus
      }));

      if (newCodeStatus === "valid") {
        Router.push(
          forUserForGuest({
            user: "/welcome",
            guest: getAppValue({ barkio: "/signup", bibino: "/signup", camerito: "/login" })
          }),
          { query: { from: "giftcard", code } }
        );
      }
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        codeStatus: "error"
      }));
    }
  }
  return (
    <PageContainer title={title} subtitle={subtitle} image={imageName}>
      <form onSubmit={handleCodeValidation}>
        <Flex mt="2rem" flexWrap={["wrap", "nowrap"]} justifyContent="center">
          {loading && (
            <Flex h={["3.5rem", "4rem"]}>
              <Spinner size="32" />
            </Flex>
          )}
          {codeStatus !== "valid" && !loading && (
            <>
              <Input
                label={t("redeem.cardPlaceholder")}
                value={code}
                attributes={{
                  autoFocus: true,
                  required: true,
                  onChange: (e) => setState({ ...state, code: e.target.value })
                }}
                className={css({ maxW: "16rem" })}
              />
              <button
                type="submit"
                className={cx(button({ variant: "filled" }), css({ ml: ["0rem", "1.5rem"], mt: ["1.5rem", "0rem"] }))}
              >
                {t("button.redeem")}
              </button>
              <input
                className={css({
                  position: "fixed",
                  right: "0",
                  top: "50%",
                  width: "5rem",
                  bg: "transparent",
                  border: "none",
                  cursor: "default",
                  outline: "none",
                  _focus: {
                    border: "none !important"
                  }
                })}
                type="text"
                aria-label="Dont fill anything to pass"
                value={state.hiddenField}
                onChange={({ target }) => setState({ ...state, hiddenField: target.value })}
              />
            </>
          )}
        </Flex>
        <Box mt="1rem" minH="4.5rem">
          {(codeStatus === "invalid" || codeStatus === "redeemed" || codeStatus === "error") && (
            <>
              <p className={css({ textAlign: "center", color: "warning" })}>
                {codeStatus === "invalid" && t("account.purchase.text.invalidPromo")}
                {codeStatus === "redeemed" && t("redeem.alreadyRedeemed")}
                {codeStatus === "error" && t("account.genericError")}
              </p>
              <p className={css({ textAlign: "center", mt: "1rem", mb: "2rem" })}>
                <Trans i18nKey="redeem.getInTouch">
                  <Link href={`mailto:${emails.supportEmail}`} className={css({ color: "primary" })} locale={false}>
                    Get in touch
                  </Link>
                </Trans>
              </p>
            </>
          )}
        </Box>
      </form>
    </PageContainer>
  );
}
